import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import { useTheme } from "@material-ui/core/styles";
import { useSetState } from "Helpers/Hooks";
import DialogContent from "@material-ui/core/DialogContent";
import Input from "Components/Input/Input";
import Grid from "@material-ui/core/Grid";
import Translator from "Languages/Utility/Translator";
import { read } from "Helpers/LocalStorage";
import showNotify from "Helpers/Notify";
import MuiButton from "Components/MuiButton/MuiButton";
import { DialogActions } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import * as Icons from "react-bootstrap-icons";
import MuiCheckBoxGroup from "Components/MuiCheckboxGroup/MuiCheckboxGroup";
import AccountService from "Services/http/endpoints/superadmin/AccountsEndpoints";
import StatusHandler from "Helpers/StatusHandler";
import * as R from "ramda";
import { convertToEnglishNumber } from "Helpers/convertToEnglishNumber";
import { useDispatch } from "react-redux";
import BackDropLoading from "Components/BackDropLoading2/BackDropLoading";
import ActionFormatter from "Components/Table/ActionFormatter/ActionFormatter";
import { getAccountingPricePeriodAgain } from "Stores/actions";

export default function EditPricePeriods({ row }) {
  const { direction } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [formValid, setFormValid] = useState({});
  const [loading, setLoading] = useState(false);
  const [checkValid, setCheckValid] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [controls, setControls] = useSetState({
    periodName: {
      type: "text",
      value: "",
      validation: {
        required: true,
      },
    },
    dayCount: {
      type: "number",
      value: "",
      validation: {
        required: true,
        isNumeric: true,
        notZero: true,
      },
    },
    order: {
      type: "number",
      value: 1,
      validation: {
        required: true,
        isNumeric: true,
        notZero: true,
      },
    },
    options: {
      type: "checkbox",
      items: [
        { value: false, key: "isActivePeriod" },
        { value: false, key: "isPeriod" },
      ],
    },
  });

  const onShowDialog = () => {
    setShowDialog(true);
  };

  const onEnter = () => {
    // reset variables
    setControls({ periodName: { value: row.Name } });
    setControls({ dayCount: { value: row.Days } });
    setControls({ order: { value: row.Order } });
    controls.options.items[0].value = row.IsActive;
    controls.options.items[1].value = row.IsDefault;
    setFormValid({ periodName: true, dayCount: true });
  };

  const setValue = (name, value, validation) => {
    setControls({ [name]: { value: value } });
    setFormValid((prev) => ({ ...prev, [name]: validation }));
  };

  const checkboxHandler = (e, name, index) => {
    const items = controls[name].items;
    items[index] = { ...items[index], value: e.currentTarget.checked };
    setControls({ [name]: { items: items } });
  };

  const onFinish = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { periodName, dayCount, order, options } = controls;
      if (
        !R.isEmpty(formValid) &&
        Object.values(formValid).every((item) => item === true)
      ) {
        const data = {
          Id: row.Id,
          KeyGroup: read("groupAKey"),
          KeyUser: read("userAKey"),
          Name: periodName.value,
          Days: +convertToEnglishNumber(dayCount.value),
          Order: +convertToEnglishNumber(order.value),
          IsActive: options.items[0].value,
          IsDefault: options.items[1].value,
        };
        const response = await AccountService.putAccountingPricePeriod(data);
        // console.log(response);
        let err = StatusHandler(response);
        if (!err.isFail) {
          if (response.data.Value.IsError === true) {
            showNotify({
              text: response.data.Value.Describ,
              customClass: "error",
            });
          } else {
            showNotify({
              text: t("mission_accomplished"),
              customClass: "success",
            });
            await dispatch(getAccountingPricePeriodAgain());
            onExit();
          }
          setTimeout(() => {
            setLoading(false);
          }, 500);
        } else {
          setLoading(false);
        }
      } else {
        setCheckValid(true);
        setLoading(false);
      }
    } catch (err) {
      console.log("[EditPricePeriods]", err);
      setLoading(false);
    }
  };

  const onExit = () => {
    setShowDialog(false);
    setLoading(false);
    setCheckValid(false);
  };

  return (
    <>
      <ActionFormatter
        title={<Translator id="editPricePeriod" />}
        icon={<Icons.Pencil />}
        onClick={onShowDialog}
        className="primary-button"
      />
      <Dialog
        dir={direction}
        open={showDialog}
        onClose={onExit}
        TransitionProps={{ onEnter: onEnter, onExit: onExit }}
        fullWidth
        maxWidth="xs"
      >
        <BackDropLoading loading={loading} />
        <div className="dialogHeader">
          <p>{<Translator id={"editPricePeriod"} />}</p>
        </div>
        <DialogContent>
          <Grid container spacing={2}>
            {Object.keys(controls).map((key) => {
              return (
                <Grid item xs={12} key={key}>
                  {(controls[key].type === "text" ||
                    controls[key].type === "number") && (
                    <Input
                      setValue={setValue.bind(this)}
                      value={controls[key].value}
                      name={key}
                      validation={controls[key].validation}
                      label={<Translator id={key} />}
                      placeholder={t(key)}
                      type={controls[key].type}
                      checkValid={checkValid}
                    />
                  )}
                  {controls[key].type === "checkbox" && (
                    <MuiCheckBoxGroup
                      translate={true}
                      name={key}
                      value={controls[key].value}
                      items={controls[key].items}
                      onChange={checkboxHandler}
                      label={<Translator id={"selectType"} />}
                      className="horizontalCheckbox"
                    />
                  )}
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <MuiButton
            color="primary"
            title={<Translator id="button_save" />}
            onClick={onFinish}
          />
          <MuiButton
            onClick={() => setShowDialog(false)}
            title={<Translator id="enseraf" />}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
